import { API_URL } from "./utils";

const ADMIN_TOKEN_KEY = "GLGIF_admin_token";
const ADMIN_USER_KEY = "GLGIF_admin_user";

/* =========================
   URL Helpers
========================= */
const API_BASE = (API_URL || "").replace(/\/+$/, ""); // remove trailing slash

function buildUrl(path = "") {
  if (!path) return API_BASE;
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${cleanPath}`;
}

/* =========================
   Session Helpers
========================= */
export function setAdminSession(token, user = {}) {
  if (!token) return;
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user || {}));
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function getAdminUser() {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_USER_KEY) || "{}");
  } catch {
    return {};
  }
}

export function clearAdminSession() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
}

/**
 * Checks:
 * 1) token exists
 * 2) if expires_at exists, ensure it has not expired
 */
export function isAdminAuthenticated() {
  const token = getAdminToken();
  if (!token) return false;

  const user = getAdminUser();
  if (user?.expires_at) {
    const expiresAtMs = new Date(user.expires_at).getTime();
    if (Number.isFinite(expiresAtMs) && Date.now() >= expiresAtMs) {
      clearAdminSession();
      return false;
    }
  }

  return true;
}

/* =========================
   Internal Helpers
========================= */
async function parseResponseOnce(res) {
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function buildHeaders({ token, isMultipart = false, headers = {} } = {}) {
  const finalHeaders = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  // Important: do NOT force Content-Type for FormData
  if (!isMultipart && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  return finalHeaders;
}

function handleAuthFailure(status) {
  if (status === 401 || status === 403) {
    clearAdminSession();
  }
}

function extractErrorMessage(data, fallback) {
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object") {
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.message === "string") return data.message;
  }
  return fallback;
}

// Optional debug (safe to keep during troubleshooting)
if (typeof window !== "undefined") {
  console.log("API_URL =", API_URL);
  console.log("API_BASE =", API_BASE);
  console.log("Login URL =", buildUrl("/admin/login"));
}

/* =========================
   Auth Endpoints
========================= */
export async function adminLogin(email, password) {
  const res = await fetch(buildUrl("/admin/login"), {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify({ email, password }),
  });

  const data = await parseResponseOnce(res);

  if (!res.ok) {
    throw new Error(extractErrorMessage(data, "Login failed"));
  }

  // Expected backend response:
  // { access_token, token_type, admin_email, expires_at }
  setAdminSession(data?.access_token, {
    email: data?.admin_email,
    role: "admin",
    expires_at: data?.expires_at,
  });

  return data;
}

export async function fetchAdminMe() {
  const token = getAdminToken();

  const res = await fetch(buildUrl("/admin/me"), {
    method: "GET",
    headers: buildHeaders({ token }),
  });

  const data = await parseResponseOnce(res);

  if (!res.ok) {
    handleAuthFailure(res.status);
    throw new Error(extractErrorMessage(data, "Unauthorized"));
  }

  return data;
}

export async function fetchAdminDashboardStats() {
  const token = getAdminToken();

  const res = await fetch(buildUrl("/admin/dashboard/stats"), {
    method: "GET",
    headers: buildHeaders({ token }),
  });

  const data = await parseResponseOnce(res);

  if (!res.ok) {
    handleAuthFailure(res.status);
    throw new Error(extractErrorMessage(data, "Failed to load stats"));
  }

  return data;
}

/* =========================
   Generic Admin Request
========================= */
export async function adminFetch(path, options = {}) {
  const token = getAdminToken();

  const {
    method = "GET",
    body,
    headers = {},
    isMultipart = false,
    ...rest
  } = options;

  const res = await fetch(buildUrl(path), {
    method,
    body,
    headers: buildHeaders({ token, isMultipart, headers }),
    ...rest,
  });

  const data = await parseResponseOnce(res);

  if (!res.ok) {
    handleAuthFailure(res.status);
    throw new Error(extractErrorMessage(data, "Request failed"));
  }

  return data;
}
